import { EventEmitter } from 'events';
import { DataConnection, Peer, PeerOptions } from 'peerjs';

export enum DataType {
  FILE = 'FILE',
  OTHER = 'OTHER',
}
export interface Data {
  dataType: DataType;
  file?: Blob;
  fileName?: string;
  fileType?: string;
  message?: string;
}

export type PeerConnectionEvents = {
  error: [Error];
  open: [];
  close: [];
  connected: [string];
  connection: [DataConnection];
};

export class PeerConnection<P> extends EventEmitter<PeerConnectionEvents> {
  peer: Peer | null = null;
  connections: Map<string, DataConnection> = new Map<string, DataConnection>();
  subscriptions: Set<(data: P) => void> = new Set<(data: P) => void>();
  config: PeerOptions;

  get id() {
    return this.peer?.id ?? '';
  }

  get isOpen() {
    return !!this.peer?.open;
  }

  constructor() {
    super();
    this.config = {
      config: { iceServers: [{ url: 'stun:stun.l.google.com:19302' }] },
      // debug: 3,
      // config: {
      //   iceServers: [
      //     {
      //       urls: 'stun:localhost:5349',
      //     },
      //   ],
      // },
    };
  }

  /**
   * Serves the peer with the given id
   */
  async serve(id: string) {
    return new Promise<string>(async (resolve, reject) => {
      this.close();
      this.peer = new Peer(id, this.config);

      this.peer.once('error', (err) => {
        reject(err);
        this.emit('error', err);
      });

      this.peer.once('open', (id) => {
        resolve(id);
        this.emit('open');
      });

      this.peer.once('close', () => {
        this.emit('close');
      });

      this.peer.on('connection', (connection) => {
        connection.once('close', () => {
          this.connections.delete(connection.peer);
        });
        this.connections.set(connection.peer, connection);
        this.emit('connection', connection);
      });
    });
  }

  /**
   * Connects to a peer with the given id
   * @param id
   */
  async connect(id: string) {
    return new Promise<void>(async (resolve, reject) => {
      this.close();
      const peer = (this.peer = new Peer(null!, this.config));

      peer.once('error', (err) => {
        reject(err);
        this.emit('error', err);
      });

      // Wait for the peer to open the socket
      peer.once('open', () => {
        const conn = peer.connect(id, {
          reliable: true,
        });

        conn.once('open', () => {
          resolve();
          this.emit('connected', id);
        });

        conn.on('data', (data) => {
          this.subscriptions.forEach((callback) => {
            callback(data as any);
          });
        });

        conn.once('error', (err) => {
          reject(err);
          this.emit('error', err);
          conn.close();
        });

        conn.once('close', () => {
          this.emit('close');
        });
      });
    });
  }

  /**
   * Sends data to the peer with the given id
   */
  async send(id: string, data: P) {
    if (!this.peer) {
      throw new Error('Peer not initialized');
    }

    // @ts-ignore
    const internalConnection = this.peer.connections[id][0];
    // @ts-ignore
    if (!internalConnection) {
      throw new Error("Connection didn't exist");
    }

    let conn = this.connections.get(id) ?? internalConnection;

    if (conn) {
      await conn.send(data);
    }
  }

  /**
   * Broadcasts data to all connected peers
   *
   */
  async broadcast(data: P) {
    await Promise.all(
      Array.from(this.connections).map(async ([, conn]) => {
        if (conn.peer !== this.id) {
          return this.send(conn.peer, data);
        } else {
          // Close the connection if it's self
          conn.close();
        }
      })
    );
  }

  /**
   * Revokes the connection with the given id
   */
  revoke(sid: string) {
    let conn = this.connections.get(sid);
    if (conn) {
      conn.close();
    }
  }

  /**
   * Subscribes to the incoming data
   */
  subscribe(callback: (data: P) => void) {
    this.subscriptions.add(callback);

    return () => {
      this.subscriptions.delete(callback);
    };
  }

  /**
   * Closes the peer connection
   */
  close() {
    if (this.peer) {
      this.peer.destroy();
      this.connections.clear();
    }
  }
}
